import type {
  SearchField,
  ComparisonOp,
  FieldFilterNode,
  HasFilterNode,
  QueryNode,
} from "@punkrecords/types";
import { tokenize } from "./tokenizer.js";

function parseComparison(value: string): { op?: ComparisonOp; value: string; numeric?: boolean } {
  const match = /^(<=|>=|!=|=|<|>)(.+)$/.exec(value);
  if (!match) {
    return { value, numeric: false };
  }
  const [, op, rest] = match;
  const trimmed = rest.trim();
  const num = Number.parseFloat(trimmed);
  const numeric = Number.isFinite(num);
  return { op: op as ComparisonOp, value: trimmed, numeric };
}

const NUMERIC_FIELDS = new Set<SearchField>(["cost", "pow", "counter", "blockicon"]);

const OP_STYLE_RE = /^(pow|power|cost|counter|blockicon|block|bi)(<=|>=|!=|<|>|=)(.+)$/i;

const FIELD_PREFIX_MAP: Record<string, SearchField> = {
  set: "set",
  s: "set",
  r: "rarity",
  rarity: "rarity",
  t: "type",
  type: "type",
  o: "text",
  oracle: "text",
  name: "name",
  id: "id",
  c: "color",
  color: "color",
  cost: "cost",
  pow: "pow",
  power: "pow",
  feature: "feature",
  art: "art",
  k: "keyword",
  keyword: "keyword",
  counter: "counter",
  trigger: "trigger",
  tr: "trigger",
  attribute: "attribute",
  attr: "attribute",
  a: "attribute",
  blockicon: "blockicon",
  block: "blockicon",
  bi: "blockicon",
};

const OP_PREFIX_TO_FIELD: Record<string, SearchField> = {
  pow: "pow",
  power: "pow",
  cost: "cost",
  counter: "counter",
  blockicon: "blockicon",
  block: "blockicon",
  bi: "blockicon",
};

const CARD_ID_RE = /^[A-Z]{1,3}\d{0,2}-\d{3}$/i;

export function parseFieldToken(raw: string): QueryNode | null {
  let token = raw;
  let negated = false;
  if (token.startsWith("-")) {
    negated = true;
    token = token.slice(1);
  }

  if (token.toLowerCase() === "or") {
    return null;
  }

  if (token.toLowerCase().startsWith("has:")) {
    const hasField = token.slice(4).toLowerCase();
    if (hasField === "trigger" || hasField === "counter") {
      const node: HasFilterNode = { kind: "has", field: hasField };
      return negated ? { kind: "not", node } : node;
    }
  }

  const colonIdx = token.indexOf(":");
  let field: SearchField = "free";
  let valuePart = token;

  // Try operator-style syntax without colon: pow<=7000, cost>=3, bi>=1
  const opMatch = OP_STYLE_RE.exec(token);
  if (colonIdx === -1 && opMatch) {
    const prefix = opMatch[1].toLowerCase();
    const mapped = OP_PREFIX_TO_FIELD[prefix];
    if (mapped) {
      field = mapped;
      valuePart = opMatch[2] + opMatch[3];
    }
  }

  // Colon-based field:value syntax
  if (colonIdx > 0 && field === "free") {
    const prefix = token.slice(0, colonIdx).toLowerCase();
    const mapped = FIELD_PREFIX_MAP[prefix];
    if (mapped) {
      field = mapped;
      valuePart = token.slice(colonIdx + 1);
    } else {
      field = "free";
      valuePart = token;
    }
  }

  let value = valuePart.trim();
  if (value.startsWith('"') && value.endsWith('"') && value.length > 1) {
    value = value.slice(1, -1);
  }

  if (field === "free" && CARD_ID_RE.test(value)) {
    field = "id";
  }

  const cmp = NUMERIC_FIELDS.has(field)
    ? parseComparison(value)
    : { value, op: undefined, numeric: false };

  const node: FieldFilterNode = {
    kind: "field",
    field,
    value: cmp.value,
    op: cmp.op,
    numeric: cmp.numeric,
  };

  return negated ? { kind: "not", node } : node;
}

export function parseQuery(input: string): QueryNode | null {
  const tokens = tokenize(input);
  if (!tokens.length) return null;

  let pos = 0;

  function peek(): string | null {
    return pos < tokens.length ? tokens[pos] : null;
  }

  function consume(): string | null {
    if (pos >= tokens.length) return null;
    const t = tokens[pos];
    pos += 1;
    return t;
  }

  function parsePrimary(): QueryNode | null {
    const t = peek();
    if (t === "(") {
      consume();
      const expr = parseExpression();
      if (peek() === ")") {
        consume();
      }
      return expr;
    }
    if (!t) return null;
    consume();
    return parseFieldToken(t);
  }

  function parseAnd(): QueryNode | null {
    const nodes: QueryNode[] = [];
    for (;;) {
      const t = peek();
      if (!t || t.toLowerCase() === "or" || t === ")") break;
      const primary = parsePrimary();
      if (primary) nodes.push(primary);
    }
    if (!nodes.length) return null;
    if (nodes.length === 1) return nodes[0];
    return { kind: "and", nodes };
  }

  function parseExpression(): QueryNode | null {
    const orNodes: QueryNode[] = [];
    const left = parseAnd();
    if (!left) return null;
    orNodes.push(left);

    while (true) {
      const t = peek();
      if (!t || t.toLowerCase() !== "or") break;
      consume();
      const right = parseAnd();
      if (!right) break;
      orNodes.push(right);
    }

    if (orNodes.length === 1) return orNodes[0];
    return { kind: "or", nodes: orNodes };
  }

  return parseExpression();
}
